import { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, Key, Activity, Shield, BarChart2, LogIn, LogOut, AlertTriangle,
  Globe, Monitor, Smartphone, RefreshCw, Search, ChevronLeft, ChevronRight,
  Clock, CheckCircle, XCircle, ArrowLeft, Loader2, User, Cpu, Database,
  TrendingUp, Calendar, Info,
} from "lucide-react";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";

// ── helpers ──────────────────────────────────────────────────────────────
function fmtDate(ts) {
  if (!ts) return null;
  try {
    return new Date(ts).toLocaleString("tr-TR", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return ts; }
}

function fmtDateShort(ts) {
  if (!ts) return null;
  try {
    return new Date(ts).toLocaleDateString("tr-TR", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return ts; }
}

const NO_DATA = (
  <span className="text-neutral-600 text-xs italic">Kayıt Yok</span>
);
const NO_DATA_MSG = "Bu veri kayıt sistemi devreye alınmadan önce tutulmamıştır.";

function Cell({ value, mono = false }) {
  if (value == null || value === "") return NO_DATA;
  return (
    <span className={mono ? "font-mono" : ""}>
      {String(value)}
    </span>
  );
}

function StatusBadge({ ok, label }) {
  return ok
    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border bg-emerald-950/60 border-emerald-800 text-emerald-300"><CheckCircle size={10}/>{label || "OK"}</span>
    : <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border bg-red-950/60 border-red-800 text-red-300"><XCircle size={10}/>{label || "FAIL"}</span>;
}

function EventTypeBadge({ type }) {
  const map = {
    login_success:  ["bg-emerald-950/60 border-emerald-800 text-emerald-300", "GİRİŞ"],
    login_failed:   ["bg-red-950/60 border-red-800 text-red-300", "BAŞARISIZ"],
    logout:         ["bg-neutral-900 border-neutral-700 text-neutral-400", "ÇIKIŞ"],
    session_expired:["bg-amber-950/60 border-amber-800 text-amber-300", "SÜRESİ DOLDU"],
    session_revoked:["bg-purple-950/60 border-purple-800 text-purple-300", "İPTAL"],
  };
  const [cls, label] = map[type] || ["bg-neutral-900 border-neutral-700 text-neutral-400", type?.toUpperCase()];
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${cls}`}>
      {label}
    </span>
  );
}

function ApiKeyEventBadge({ type }) {
  const map = {
    created: ["bg-emerald-950/60 border-emerald-800 text-emerald-300", "OLUŞTURULDU"],
    rotated: ["bg-blue-950/60 border-blue-800 text-blue-300", "ROTASYON"],
    revoked: ["bg-red-950/60 border-red-800 text-red-300", "İPTAL"],
    deleted: ["bg-neutral-900 border-neutral-700 text-neutral-500", "SİLİNDİ"],
    used:    ["bg-purple-950/60 border-purple-800 text-purple-300", "KULLANILDI"],
  };
  const [cls, label] = map[type] || ["bg-neutral-900 border-neutral-700 text-neutral-400", type?.toUpperCase()];
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${cls}`}>
      {label}
    </span>
  );
}

function DeviceIcon({ device }) {
  if (device === "Mobile") return <Smartphone size={13} className="text-neutral-500 inline"/>;
  if (device === "Tablet") return <Monitor size={13} className="text-neutral-500 inline"/>;
  return <Monitor size={13} className="text-neutral-500 inline"/>;
}

function StatCard({ icon: Icon, label, value, sub, tone = "neutral" }) {
  const toneMap = {
    neutral: "text-white",
    green:   "text-emerald-300",
    red:     "text-red-300",
    amber:   "text-amber-300",
    blue:    "text-blue-300",
  };
  return (
    <div className="bg-neutral-950 border border-neutral-800 rounded-sm p-5">
      <div className="flex items-center gap-2 text-neutral-500 mb-2">
        <Icon size={14} className="text-red-500"/>
        <span className="text-[10px] font-mono uppercase tracking-wider">{label}</span>
      </div>
      <p className={`headline text-3xl ${toneMap[tone]}`}>{value ?? "—"}</p>
      {sub && <p className="text-[10px] font-mono text-neutral-600 mt-1">{sub}</p>}
    </div>
  );
}

function Pagination({ page, total, pageSize, onChange }) {
  const totalPages = Math.ceil(total / pageSize);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-800 text-xs text-neutral-500">
      <span>{total.toLocaleString()} kayıt · Sayfa {page}/{Math.max(1, totalPages)}</span>
      <div className="flex gap-1">
        <button
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
          className="p-1.5 border border-neutral-800 rounded-sm hover:bg-neutral-900 disabled:opacity-40"
        ><ChevronLeft size={13}/></button>
        <button
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages}
          className="p-1.5 border border-neutral-800 rounded-sm hover:bg-neutral-900 disabled:opacity-40"
        ><ChevronRight size={13}/></button>
      </div>
    </div>
  );
}

const inputCls = "bg-neutral-950 border border-neutral-800 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-600";
const selectCls = `${inputCls} pr-8`;

// ── Dashboard ─────────────────────────────────────────────────────────────
function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/control/overview");
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-red-500" size={28}/></div>;
  if (!data) return null;

  return (
    <div className="space-y-8" data-testid="cc-dashboard">
      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Toplam Kullanıcı" value={data.total_users}/>
        <StatCard icon={Calendar} label="Bugün Yeni" value={data.new_users_today} tone="green"/>
        <StatCard icon={TrendingUp} label="Son 7 Gün Yeni" value={data.new_users_7d}/>
        <StatCard icon={Calendar} label="Son 30 Gün Yeni" value={data.new_users_30d}/>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={LogIn} label="Bugün Başarılı Giriş" value={data.logins_today_success} tone="green"/>
        <StatCard icon={AlertTriangle} label="Bugün Başarısız" value={data.logins_today_failed} tone="red"/>
        <StatCard icon={Key} label="Aktif API Key" value={data.active_api_keys}/>
        <StatCard icon={Activity} label="Bugün API Çağrısı" value={data.api_calls_today} tone="blue"/>
      </div>

      {/* Recent streams */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent users */}
        <div>
          <h3 className="eyebrow text-neutral-500 mb-3">SON ÜYELER</h3>
          <div className="bg-neutral-950 border border-neutral-800 rounded-sm">
            {data.recent_users.length === 0
              ? <p className="px-4 py-6 text-neutral-600 text-sm">Henüz kullanıcı yok</p>
              : data.recent_users.map((u, i) => (
              <div key={u.id || i} className="flex items-center justify-between px-4 py-3 border-b border-neutral-800/50 last:border-0">
                <div>
                  <p className="font-mono text-sm text-white">{u.email}</p>
                  <p className="text-[10px] font-mono text-neutral-500">{u.plan?.toUpperCase()} · {fmtDate(u.created_at)}</p>
                </div>
                {u.is_admin && <span className="px-2 py-0.5 text-[9px] bg-red-600 text-white rounded-sm font-bold">ADMIN</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Recent logins */}
        <div>
          <h3 className="eyebrow text-neutral-500 mb-3">SON GİRİŞLER</h3>
          <div className="bg-neutral-950 border border-neutral-800 rounded-sm">
            {data.recent_logins.length === 0
              ? <p className="px-4 py-6 text-neutral-600 text-sm">Henüz kayıt yok</p>
              : data.recent_logins.map((l, i) => (
              <div key={l.id || i} className="flex items-center justify-between px-4 py-3 border-b border-neutral-800/50 last:border-0">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <EventTypeBadge type={l.event_type}/>
                    <p className="font-mono text-xs text-neutral-300 truncate">{l.email}</p>
                  </div>
                  <p className="text-[10px] font-mono text-neutral-600 mt-0.5">
                    {l.browser} · {l.os} · {l.ip}
                  </p>
                </div>
                <p className="text-[10px] font-mono text-neutral-600 flex-shrink-0 ml-2">{fmtDate(l.created_at)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Recent API key events */}
        <div>
          <h3 className="eyebrow text-neutral-500 mb-3">SON API KEY HAREKETLERİ</h3>
          <div className="bg-neutral-950 border border-neutral-800 rounded-sm">
            {data.recent_api_key_events.length === 0
              ? <p className="px-4 py-6 text-neutral-600 text-sm">Henüz kayıt yok</p>
              : data.recent_api_key_events.map((ev, i) => (
              <div key={ev.id || i} className="flex items-center justify-between px-4 py-3 border-b border-neutral-800/50 last:border-0">
                <div>
                  <div className="flex items-center gap-2">
                    <ApiKeyEventBadge type={ev.event_type}/>
                    <p className="font-mono text-xs text-neutral-300">{ev.api_key_prefix}</p>
                  </div>
                  <p className="text-[10px] font-mono text-neutral-600">{ev.email} · {ev.plan}</p>
                </div>
                <p className="text-[10px] font-mono text-neutral-600">{fmtDate(ev.created_at)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Recent API requests */}
        <div>
          <h3 className="eyebrow text-neutral-500 mb-3">SON API ÇAĞRILARI</h3>
          <div className="bg-neutral-950 border border-neutral-800 rounded-sm">
            {data.recent_api_requests.length === 0
              ? <p className="px-4 py-6 text-neutral-600 text-sm">Henüz kayıt yok</p>
              : data.recent_api_requests.map((r, i) => (
              <div key={r.id || i} className="flex items-center justify-between px-4 py-3 border-b border-neutral-800/50 last:border-0">
                <div>
                  <div className="flex items-center gap-2">
                    <StatusBadge ok={r.success}/>
                    <p className="font-mono text-xs text-neutral-300">{r.endpoint}</p>
                  </div>
                  <p className="text-[10px] font-mono text-neutral-600">{r.api_key_prefix} · {r.ip}</p>
                </div>
                <p className="text-[10px] font-mono text-neutral-600">{fmtDate(r.occurred_at)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={load} className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-white border border-neutral-800 px-3 py-2 rounded-sm hover:bg-neutral-900">
          <RefreshCw size={12}/> Yenile
        </button>
      </div>
    </div>
  );
}

// ── Users Table ───────────────────────────────────────────────────────────
function UsersTab() {
  const [data, setData] = useState({ users: [], total: 0, page: 1, page_size: 25 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedUser, setSelectedUser] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/control/users", {
        params: { search: search || undefined, page, page_size: pageSize },
      });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, [search, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const cols = [
    "Ad Soyad", "Email", "Rol", "Durum", "Üyelik Tarihi",
    "İlk Kaydedilen Giriş", "Son Giriş", "Son Çıkış", "Son Aktivite",
    "Giriş Sayısı", "Başarısız", "Son IP", "Browser", "OS",
    "API Key", "Son API",
  ];

  return (
    <div data-testid="cc-users-tab">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"/>
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Email, ad veya kullanıcı ID..."
            className={`pl-9 pr-3 py-2 text-sm w-full ${inputCls}`}
            data-testid="cc-user-search"
          />
        </div>
        <select
          value={pageSize}
          onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
          className={`${selectCls} text-sm py-2`}
        >
          {[25, 50, 100].map(n => <option key={n} value={n}>{n} / sayfa</option>)}
        </select>
        <button onClick={load} className="p-2 border border-neutral-800 rounded-sm hover:bg-neutral-900 text-neutral-400">
          <RefreshCw size={14}/>
        </button>
      </div>

      <div className="bg-neutral-950 border border-neutral-800 rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[1200px]">
          <thead className="bg-neutral-900 text-neutral-400 text-[10px] uppercase tracking-wider">
            <tr>
              {cols.map(c => (
                <th key={c} className="text-left px-3 py-3 whitespace-nowrap">{c}</th>
              ))}
              <th className="px-3 py-3 text-right">Detay</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {loading && data.users.length === 0 && (
              <tr><td colSpan={cols.length + 1} className="px-4 py-12 text-center">
                <Loader2 className="animate-spin text-red-500 inline"/>
              </td></tr>
            )}
            {!loading && data.users.length === 0 && (
              <tr><td colSpan={cols.length + 1} className="px-4 py-12 text-center text-neutral-600">
                Kullanıcı bulunamadı
              </td></tr>
            )}
            {data.users.map((u) => (
              <tr
                key={u.id}
                className="hover:bg-neutral-900/40 cursor-pointer"
                data-testid={`cc-user-row-${u.id}`}
              >
                <td className="px-3 py-2.5 text-white">{u.name || "—"}</td>
                <td className="px-3 py-2.5 font-mono text-xs text-neutral-300">
                  {u.email}
                  {u.is_admin && <span className="ml-1 px-1.5 py-0.5 text-[9px] bg-red-600 text-white rounded-sm">ADMIN</span>}
                </td>
                <td className="px-3 py-2.5 uppercase text-[10px] font-bold text-red-400">{u.plan}</td>
                <td className="px-3 py-2.5">
                  {u.account_status === "suspended"
                    ? <span className="text-[10px] font-bold text-red-400 uppercase">Askıda</span>
                    : <span className="text-[10px] font-bold text-emerald-400 uppercase">Aktif</span>}
                </td>
                <td className="px-3 py-2.5 font-mono text-[10px] text-neutral-400">{fmtDateShort(u.created_at) || NO_DATA}</td>
                <td className="px-3 py-2.5 font-mono text-[10px] text-neutral-400">
                  {u.first_tracked_login_at ? fmtDate(u.first_tracked_login_at) : NO_DATA}
                </td>
                <td className="px-3 py-2.5 font-mono text-[10px] text-neutral-400">
                  {u.last_login_at ? fmtDate(u.last_login_at) : NO_DATA}
                </td>
                <td className="px-3 py-2.5 font-mono text-[10px] text-neutral-400">
                  {u.last_explicit_logout_at ? fmtDate(u.last_explicit_logout_at) : NO_DATA}
                </td>
                <td className="px-3 py-2.5 font-mono text-[10px]">
                  {u.last_activity_at
                    ? <span className="text-neutral-300">{fmtDate(u.last_activity_at)}<br/>
                        <span className="text-neutral-600">{u.last_activity_source}</span>
                      </span>
                    : NO_DATA}
                </td>
                <td className="px-3 py-2.5 font-mono text-white text-center">{u.login_count ?? 0}</td>
                <td className="px-3 py-2.5 font-mono text-center">
                  <span className={u.recent_failed_login_count > 0 ? "text-red-400" : "text-neutral-600"}>
                    {u.recent_failed_login_count ?? 0}
                  </span>
                </td>
                <td className="px-3 py-2.5 font-mono text-[10px] text-neutral-500">{u.last_login_ip || NO_DATA}</td>
                <td className="px-3 py-2.5 text-[10px] text-neutral-400">
                  {u.last_browser ? <><DeviceIcon device={u.last_device}/> {u.last_browser}</> : NO_DATA}
                </td>
                <td className="px-3 py-2.5 text-[10px] text-neutral-400">{u.last_os || NO_DATA}</td>
                <td className="px-3 py-2.5 font-mono text-center text-white">{u.api_key_count ?? 0}</td>
                <td className="px-3 py-2.5 font-mono text-[10px] text-neutral-500">
                  {u.api_key_last_used_at ? fmtDate(u.api_key_last_used_at) : NO_DATA}
                </td>
                <td className="px-3 py-2.5 text-right">
                  <button
                    onClick={() => setSelectedUser(u)}
                    className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-neutral-700 text-neutral-400 hover:border-red-600 hover:text-red-400 rounded-sm"
                    data-testid={`cc-user-detail-btn-${u.id}`}
                  >
                    Detay
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination page={page} total={data.total} pageSize={pageSize} onChange={(p) => setPage(p)}/>
      </div>

      {/* User detail drawer */}
      <UserDetailSheet userId={selectedUser?.id} open={!!selectedUser} onClose={() => setSelectedUser(null)}/>
    </div>
  );
}

// ── User Detail Sheet ─────────────────────────────────────────────────────
function UserDetailSheet({ userId, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    api.get(`/admin/control/user/${userId}`)
      .then(r => setData(r.data))
      .catch(e => toast.error(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [open, userId]);

  const SectionTitle = ({ children }) => (
    <h4 className="eyebrow text-neutral-500 text-[10px] uppercase tracking-wider mb-2 mt-5 first:mt-0">{children}</h4>
  );

  const Row = ({ label, value, mono = false }) => (
    <div className="flex items-start justify-between gap-4 py-1.5 border-b border-neutral-800/50 last:border-0">
      <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 flex-shrink-0 w-40">{label}</span>
      <span className={`text-xs text-right ${mono ? "font-mono text-neutral-300" : "text-white"}`}>
        {value == null ? <span className="text-neutral-600 italic">Kayıt Yok</span> : String(value)}
      </span>
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl bg-neutral-950 border-l border-neutral-800 overflow-y-auto">
        <SheetHeader className="mb-4">
          <SheetTitle className="headline text-2xl text-white flex items-center gap-2">
            <User size={20} className="text-red-500"/> Kullanıcı Detayı
          </SheetTitle>
          {data?.email && (
            <SheetDescription className="font-mono text-xs text-neutral-400">{data.email}</SheetDescription>
          )}
        </SheetHeader>

        {loading && (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-red-500" size={28}/></div>
        )}

        {!loading && data && (
          <div data-testid={`cc-user-detail-${userId}`}>
            {/* Identity */}
            <SectionTitle>Kimlik</SectionTitle>
            <div className="bg-neutral-900 border border-neutral-800 rounded-sm px-4 py-2">
              <Row label="ID" value={data.id} mono/>
              <Row label="Email" value={data.email} mono/>
              <Row label="Ad" value={data.name}/>
              <Row label="Rol" value={data.is_admin ? "ADMIN" : "Kullanıcı"}/>
              <Row label="Plan" value={data.plan?.toUpperCase()}/>
              <Row label="Durum" value={data.account_status === "suspended" ? "⚠ Askıda" : "Aktif"}/>
              <Row label="Kredi" value={`${(data.credits || 0).toLocaleString()} kredi`}/>
            </div>

            {/* Dates */}
            <SectionTitle>Tarihler</SectionTitle>
            <div className="bg-neutral-900 border border-neutral-800 rounded-sm px-4 py-2">
              <Row label="Üyelik Tarihi" value={fmtDate(data.created_at)} mono/>
              <Row label="İlk Kaydedilen Giriş" value={data.first_tracked_login_at ? fmtDate(data.first_tracked_login_at) : null} mono/>
              <Row label="Son Giriş" value={data.last_login_at ? fmtDate(data.last_login_at) : null} mono/>
              <Row label="Son Manuel Çıkış" value={data.last_explicit_logout_at ? fmtDate(data.last_explicit_logout_at) : null} mono/>
              <Row label="Son Aktivite" value={data.last_activity_at ? `${fmtDate(data.last_activity_at)} (${data.last_activity_source})` : null} mono/>
            </div>
            {!data.first_tracked_login_at && (
              <p className="text-[10px] text-neutral-600 italic mt-1 flex items-center gap-1">
                <Info size={10}/> {NO_DATA_MSG}
              </p>
            )}

            {/* Login stats */}
            <SectionTitle>Giriş İstatistikleri</SectionTitle>
            <div className="bg-neutral-900 border border-neutral-800 rounded-sm px-4 py-2">
              <Row label="Toplam Giriş" value={data.login_count ?? 0}/>
              <Row label="Başarısız (7g)" value={data.recent_failed_login_count ?? 0}/>
              <Row label="Son IP" value={data.last_login_ip || null} mono/>
              <Row label="Browser" value={data.last_browser || null}/>
              <Row label="OS" value={data.last_os || null}/>
              <Row label="Cihaz" value={data.last_device || null}/>
            </div>

            {/* Recent logins */}
            {data.recent_logins?.length > 0 && (
              <>
                <SectionTitle>Son Giriş Geçmişi</SectionTitle>
                <div className="bg-neutral-900 border border-neutral-800 rounded-sm overflow-hidden">
                  {data.recent_logins.slice(0, 10).map((l, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 border-b border-neutral-800/50 last:border-0 text-xs">
                      <div className="flex items-center gap-2">
                        <EventTypeBadge type={l.event_type}/>
                        <span className="font-mono text-neutral-500">{l.ip} · {l.browser}</span>
                      </div>
                      <span className="font-mono text-neutral-600">{fmtDate(l.created_at)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* API Keys */}
            <SectionTitle>API Keyler ({data.api_key_count ?? 0})</SectionTitle>
            {data.api_keys?.length > 0 ? (
              <div className="space-y-2">
                {data.api_keys.map((k, i) => (
                  <div key={i} className="bg-neutral-900 border border-neutral-800 rounded-sm p-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-mono text-xs text-white">{k.masked_key || k.key_prefix}</p>
                      <div className="flex gap-1">
                        <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase border border-neutral-700 text-neutral-400 rounded-sm">{k.plan}</span>
                        {k.legacy_storage && <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase border border-amber-800 text-amber-400 bg-amber-950/30 rounded-sm">LEGACY</span>}
                        {k.revoked_at && <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase border border-red-800 text-red-400 bg-red-950/30 rounded-sm">İPTAL</span>}
                      </div>
                    </div>
                    <p className="text-[10px] font-mono text-neutral-600">
                      Oluşturuldu: {fmtDate(k.created_at)} · Son kullanım: {k.last_used_at ? fmtDate(k.last_used_at) : "Kullanılmadı"}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-neutral-600 italic">{NO_DATA_MSG}</p>
            )}

            {/* Recent API requests */}
            {data.recent_api_requests?.length > 0 && (
              <>
                <SectionTitle>Son API Çağrıları</SectionTitle>
                <div className="bg-neutral-900 border border-neutral-800 rounded-sm overflow-hidden">
                  {data.recent_api_requests.map((r, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 border-b border-neutral-800/50 last:border-0 text-xs">
                      <div className="flex items-center gap-2">
                        <StatusBadge ok={r.success}/>
                        <span className="font-mono text-neutral-300">{r.endpoint}</span>
                      </div>
                      <span className="font-mono text-neutral-600">{fmtDate(r.occurred_at)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Content events */}
            {data.content_events?.length > 0 && (
              <>
                <SectionTitle>İçerik Hareketleri</SectionTitle>
                <div className="bg-neutral-900 border border-neutral-800 rounded-sm overflow-hidden">
                  {data.content_events.map((ev, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 border-b border-neutral-800/50 last:border-0 text-xs">
                      <span className="uppercase text-[10px] font-bold text-neutral-400">{ev.action}</span>
                      <span className="font-mono text-[10px] text-neutral-600">{fmtDate(ev.created_at)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Credit ledger */}
            {data.credit_ledger?.length > 0 && (
              <>
                <SectionTitle>Ödeme Hareketleri (Son {data.credit_ledger.length})</SectionTitle>
                <div className="bg-neutral-900 border border-neutral-800 rounded-sm overflow-hidden">
                  {data.credit_ledger.map((l, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 border-b border-neutral-800/50 last:border-0 text-xs">
                      <div>
                        <span className={`font-mono ${l.delta > 0 ? "text-emerald-400" : "text-red-400"}`}>
                          {l.delta > 0 ? "+" : ""}{l.delta.toLocaleString()}
                        </span>
                        <span className="text-neutral-500 ml-2">{l.reason}</span>
                      </div>
                      <span className="font-mono text-[10px] text-neutral-600">{fmtDate(l.created_at)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="h-8"/>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── Login History ─────────────────────────────────────────────────────────
function LoginHistoryTab() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [eventType, setEventType] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/control/login-history", {
        params: {
          event_type: eventType || undefined,
          email: emailFilter || undefined,
          page, page_size: pageSize,
        },
      });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, [eventType, emailFilter, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="cc-login-history-tab">
      <div className="flex flex-wrap gap-2 mb-4">
        <select
          value={eventType}
          onChange={(e) => { setEventType(e.target.value); setPage(1); }}
          className={`${selectCls} text-sm py-2`}
          data-testid="cc-login-filter-type"
        >
          <option value="">Tüm Olaylar</option>
          <option value="login_success">Başarılı Giriş</option>
          <option value="login_failed">Başarısız Giriş</option>
          <option value="logout">Çıkış</option>
          <option value="session_expired">Oturum Süresi Doldu</option>
        </select>
        <input
          value={emailFilter}
          onChange={(e) => { setEmailFilter(e.target.value); setPage(1); }}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Email filtrele..."
          className={`${inputCls} text-sm py-2 flex-1 min-w-[160px]`}
          data-testid="cc-login-filter-email"
        />
        <select
          value={pageSize}
          onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
          className={`${selectCls} text-sm py-2`}
        >
          {[25, 50, 100].map(n => <option key={n} value={n}>{n} / sayfa</option>)}
        </select>
        <button onClick={load} className="p-2 border border-neutral-800 rounded-sm hover:bg-neutral-900 text-neutral-400">
          <RefreshCw size={14}/>
        </button>
      </div>

      <div className="bg-neutral-950 border border-neutral-800 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-900 text-neutral-400 text-[10px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Olay</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">IP</th>
              <th className="text-left px-4 py-3">Browser · OS</th>
              <th className="text-left px-4 py-3">Neden</th>
              <th className="text-left px-4 py-3">Admin</th>
              <th className="text-left px-4 py-3">Tarih</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {loading && data.items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center"><Loader2 className="animate-spin text-red-500 inline"/></td></tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-16 text-center text-neutral-600">
                Henüz kayıt yok. İlk giriş sonrası bu tablo dolmaya başlayacak.
              </td></tr>
            )}
            {data.items.map((l, i) => (
              <tr key={l.id || i} className="hover:bg-neutral-900/40" data-testid={`cc-login-row-${i}`}>
                <td className="px-4 py-2.5"><EventTypeBadge type={l.event_type}/></td>
                <td className="px-4 py-2.5 font-mono text-xs text-neutral-300">{l.email}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-neutral-500">{l.ip || "—"}</td>
                <td className="px-4 py-2.5 text-xs text-neutral-400">
                  {l.browser || "—"} · {l.os || "—"}
                </td>
                <td className="px-4 py-2.5 text-[10px] font-mono text-amber-400">
                  {l.failure_reason || "—"}
                </td>
                <td className="px-4 py-2.5 text-center">
                  {l.is_admin && <span className="px-1.5 py-0.5 text-[9px] bg-red-600 text-white rounded-sm font-bold">A</span>}
                </td>
                <td className="px-4 py-2.5 font-mono text-[10px] text-neutral-500">{fmtDate(l.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination page={page} total={data.total} pageSize={pageSize} onChange={setPage}/>
      </div>
    </div>
  );
}

// ── API Keys ──────────────────────────────────────────────────────────────
function ApiKeysTab() {
  const [data, setData] = useState({ keys: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/control/api-keys", {
        params: {
          search: search || undefined,
          plan: planFilter || undefined,
          page, page_size: pageSize,
        },
      });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, [search, planFilter, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="cc-api-keys-tab">
      <div className="flex flex-wrap gap-2 mb-4">
        <input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Email, key prefix veya isim..."
          className={`${inputCls} text-sm py-2 flex-1 min-w-[180px]`}
        />
        <select
          value={planFilter}
          onChange={(e) => { setPlanFilter(e.target.value); setPage(1); }}
          className={`${selectCls} text-sm py-2`}
        >
          <option value="">Tüm Planlar</option>
          <option value="free">Free</option>
          <option value="pro">Pro</option>
          <option value="enterprise">Enterprise</option>
        </select>
        <select
          value={pageSize}
          onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
          className={`${selectCls} text-sm py-2`}
        >
          {[25, 50, 100].map(n => <option key={n} value={n}>{n} / sayfa</option>)}
        </select>
        <button onClick={load} className="p-2 border border-neutral-800 rounded-sm hover:bg-neutral-900 text-neutral-400">
          <RefreshCw size={14}/>
        </button>
      </div>

      <div className="bg-neutral-950 border border-neutral-800 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-900 text-neutral-400 text-[10px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Key</th>
              <th className="text-left px-4 py-3">İsim</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Plan</th>
              <th className="text-left px-4 py-3">Oluşturuldu</th>
              <th className="text-left px-4 py-3">Son Kullanım</th>
              <th className="text-left px-4 py-3">Durum</th>
              <th className="text-left px-4 py-3">Son Hareket</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {loading && data.keys.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center"><Loader2 className="animate-spin text-red-500 inline"/></td></tr>
            )}
            {!loading && data.keys.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-neutral-600">API key bulunamadı</td></tr>
            )}
            {data.keys.map((k, i) => (
              <tr key={k.id || i} className="hover:bg-neutral-900/40" data-testid={`cc-key-row-${k.id}`}>
                <td className="px-4 py-2.5 font-mono text-xs text-white">
                  {k.masked_key}
                  {k.legacy_storage && <span className="ml-1 px-1.5 py-0.5 text-[9px] font-bold border border-amber-800 text-amber-400 bg-amber-950/30 rounded-sm">LEGACY</span>}
                </td>
                <td className="px-4 py-2.5 text-neutral-300 text-xs">{k.name || "—"}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-neutral-400">{k.email || "—"}</td>
                <td className="px-4 py-2.5 uppercase text-[10px] font-bold text-red-400">{k.plan}</td>
                <td className="px-4 py-2.5 font-mono text-[10px] text-neutral-500">{fmtDate(k.created_at)}</td>
                <td className="px-4 py-2.5 font-mono text-[10px] text-neutral-500">
                  {k.last_used_at ? fmtDate(k.last_used_at) : NO_DATA}
                </td>
                <td className="px-4 py-2.5">
                  {k.revoked_at
                    ? <span className="text-[10px] font-bold text-red-400 uppercase">İptal</span>
                    : <span className="text-[10px] font-bold text-emerald-400 uppercase">Aktif</span>}
                </td>
                <td className="px-4 py-2.5">
                  {k.last_event
                    ? <ApiKeyEventBadge type={k.last_event.event_type}/>
                    : NO_DATA}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination page={page} total={data.total} pageSize={pageSize} onChange={setPage}/>
      </div>
    </div>
  );
}

// ── API Requests ──────────────────────────────────────────────────────────
function ApiRequestsTab() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [successFilter, setSuccessFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (successFilter === "true") params.success = true;
      if (successFilter === "false") params.success = false;
      const { data: d } = await api.get("/admin/control/api-requests", { params });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, [successFilter, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="cc-api-requests-tab">
      <div className="flex flex-wrap gap-2 mb-4">
        <select
          value={successFilter}
          onChange={(e) => { setSuccessFilter(e.target.value); setPage(1); }}
          className={`${selectCls} text-sm py-2`}
        >
          <option value="">Tüm Çağrılar</option>
          <option value="true">Başarılı</option>
          <option value="false">Başarısız</option>
        </select>
        <select
          value={pageSize}
          onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
          className={`${selectCls} text-sm py-2`}
        >
          {[25, 50, 100].map(n => <option key={n} value={n}>{n} / sayfa</option>)}
        </select>
        <button onClick={load} className="p-2 border border-neutral-800 rounded-sm hover:bg-neutral-900 text-neutral-400">
          <RefreshCw size={14}/>
        </button>
      </div>

      <div className="bg-neutral-950 border border-neutral-800 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-900 text-neutral-400 text-[10px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Durum</th>
              <th className="text-left px-4 py-3">Endpoint</th>
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3">Key Prefix</th>
              <th className="text-left px-4 py-3">IP</th>
              <th className="text-right px-4 py-3">Süre</th>
              <th className="text-left px-4 py-3">Kredi</th>
              <th className="text-left px-4 py-3">Tarih</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {loading && data.items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center"><Loader2 className="animate-spin text-red-500 inline"/></td></tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-16 text-center text-neutral-600">
                Henüz API çağrısı yok. İlk paid API çağrısı sonrası dolmaya başlayacak.
              </td></tr>
            )}
            {data.items.map((r, i) => (
              <tr key={r.id || i} className="hover:bg-neutral-900/40">
                <td className="px-4 py-2.5"><StatusBadge ok={r.success}/></td>
                <td className="px-4 py-2.5 font-mono text-xs text-neutral-300">{r.endpoint}</td>
                <td className="px-4 py-2.5 font-mono text-[10px] text-neutral-500">{r.user_id?.slice(0, 8) || "—"}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-neutral-400">{r.api_key_prefix || "—"}</td>
                <td className="px-4 py-2.5 font-mono text-[10px] text-neutral-500">{r.ip || "—"}</td>
                <td className="px-4 py-2.5 text-right font-mono text-xs text-neutral-400">
                  {r.duration_ms != null ? `${r.duration_ms}ms` : "—"}
                </td>
                <td className="px-4 py-2.5">
                  {r.credit_used
                    ? <span className="text-[10px] text-amber-400">-1 kredi</span>
                    : <span className="text-[10px] text-neutral-600">—</span>}
                </td>
                <td className="px-4 py-2.5 font-mono text-[10px] text-neutral-500">{fmtDate(r.occurred_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination page={page} total={data.total} pageSize={pageSize} onChange={setPage}/>
      </div>
    </div>
  );
}

// ── Coming Soon placeholder ───────────────────────────────────────────────
function ComingSoon({ label }) {
  return (
    <div className="py-24 text-center border border-dashed border-neutral-800 rounded-sm">
      <Clock size={32} className="mx-auto mb-3 text-neutral-700"/>
      <p className="eyebrow text-neutral-600">{label}</p>
      <p className="text-xs text-neutral-700 mt-1">Bu sekme gelecek fazlarda eklenecek.</p>
    </div>
  );
}

// ── Main Control Center Page ──────────────────────────────────────────────
const TABS = [
  ["dashboard", BarChart2, "Dashboard"],
  ["users",     Users,     "Kullanıcılar"],
  ["logins",    LogIn,     "Giriş Geçmişi"],
  ["apikeys",   Key,       "API Keyler"],
  ["apireqs",   Activity,  "API Çağrıları"],
  ["security",  Shield,    "Güvenlik Olayları"],
  ["system",    Database,  "Sistem Hataları"],
  ["audit",     Cpu,       "Audit Trail"],
];
const COMING_SOON_TABS = new Set(["security", "system", "audit"]);

export default function ControlCenter() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("dashboard");

  if (loading) return <div className="py-32 flex justify-center"><Loader2 className="animate-spin text-red-500" size={32}/></div>;
  if (!user) return <Navigate to="/login?redirect=/admin/control-center" replace/>;
  if (!user.is_admin) return <Navigate to="/" replace/>;

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-8" data-testid="control-center-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div className="flex items-center gap-3">
          <Shield className="text-red-500 flex-shrink-0" size={28}/>
          <div>
            <p className="eyebrow text-red-500">CONTROL CENTER · loft.press</p>
            <h1 className="headline text-4xl text-white mt-1">Denetim Merkezi</h1>
            <p className="text-xs font-mono text-neutral-500 mt-1">Yalnızca admin erişimi · Gerçek zamanlı audit & izleme</p>
          </div>
        </div>
        <Link
          to="/admin"
          className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-white border border-neutral-800 px-3 py-2 rounded-sm hover:bg-neutral-900"
          data-testid="cc-back-to-admin"
        >
          <ArrowLeft size={13}/> Admin Paneli
        </Link>
      </div>

      {/* Tab bar */}
      <div className="flex flex-wrap items-center gap-1 border-b border-neutral-800 mb-6">
        {TABS.map(([id, Icon, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
              tab === id
                ? "border-red-500 text-red-500"
                : "border-transparent text-neutral-500 hover:text-white"
            }`}
            data-testid={`cc-tab-${id}`}
          >
            <Icon size={13}/>
            {label}
            {COMING_SOON_TABS.has(id) && (
              <span className="px-1 py-0.5 text-[8px] font-bold uppercase bg-neutral-800 text-neutral-500 rounded-sm ml-1">YAKINDA</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "dashboard" && <Dashboard/>}
      {tab === "users"     && <UsersTab/>}
      {tab === "logins"    && <LoginHistoryTab/>}
      {tab === "apikeys"   && <ApiKeysTab/>}
      {tab === "apireqs"   && <ApiRequestsTab/>}
      {tab === "security"  && <ComingSoon label="GÜVENLİK OLAYLARI — PHASE 2"/>}
      {tab === "system"    && <ComingSoon label="SİSTEM HATALARI — PHASE 2"/>}
      {tab === "audit"     && <ComingSoon label="AUDIT TRAIL — PHASE 2"/>}
    </div>
  );
}
